
import { useState } from 'react';
import { toast } from 'sonner';
import { ItineraryEvent, Trip } from '@/types/itinerary';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';

interface UseEventFormProps {
  trip: Trip | null;
  event: ItineraryEvent | null;
  onClose: () => void;
  selectedDate: string;
}

export const useEventForm = ({ trip, event, onClose, selectedDate }: UseEventFormProps) => {
  const { user } = useAuth();
  const isEditMode = !!event;
  
  const [formData, setFormData] = useState<Partial<ItineraryEvent>>({
    title: event?.title || '',
    date: event?.date || selectedDate,
    time: event?.time || '',
    end_time: event?.end_time || '',
    location: event?.location || '',
    description: event?.description || '',
    isTransportation: event?.isTransportation || false,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(true);
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData({ ...formData, [name]: checked });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!trip?.id || !user) {
      toast.error("You must be logged in to add events");
      return;
    }
    
    if (!formData.title) {
      toast.error("Title is required");
      return;
    }
    
    if (!formData.date) {
      toast.error("Date is required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data with transportation flags
      const isTransportation = formData.isTransportation || false;
      let processedDescription = formData.description || '';
      
      if (isTransportation) {
        processedDescription = `[TRANSPORTATION] ${processedDescription}`;
      }
      
      // Create a data object for database
      const dataToSave: any = {
        title: formData.title,
        date: formData.date,
        time: formData.time || '',
        end_time: formData.end_time || '',
        location: formData.location || '',
        description: processedDescription,
        updated_at: new Date().toISOString(),
        is_transportation: isTransportation,
        transportation_type: isTransportation ? 'other' : null,
      };
      
      if (isEditMode && event) {
        // Update existing event
        const { error } = await supabase
          .from('itinerary_events')
          .update(dataToSave)
          .eq('id', event.id);
          
        if (error) throw error;
        toast.success("Event updated successfully");
      } else {
        // Create new event
        // Get the current highest order_index for the date
        const { data: existingEvents } = await supabase
          .from('itinerary_events')
          .select('order_index')
          .eq('trip_id', trip.id)
          .eq('date', formData.date)
          .order('order_index', { ascending: false })
          .limit(1);
          
        const highestIndex = existingEvents && existingEvents.length > 0 
          ? existingEvents[0].order_index 
          : -1;
          
        const { error } = await supabase
          .from('itinerary_events')
          .insert({
            trip_id: trip.id,
            title: formData.title,
            date: formData.date,
            time: formData.time || '',
            end_time: formData.end_time || '',
            location: formData.location || '',
            description: processedDescription,
            created_by: user.id,
            order_index: highestIndex + 1,
            is_transportation: isTransportation,
            transportation_type: isTransportation ? 'other' : null,
          });
          
        if (error) throw error;
        toast.success("Event added successfully");
      }
      
      // Close the form and notify parent component
      handleClose();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(isEditMode ? "Failed to update event" : "Failed to add event");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    setOpen(false);
    onClose();
  };
  
  return {
    formData,
    isSubmitting,
    open,
    isEditMode,
    handleChange,
    handleSwitchChange,
    handleSubmit,
    handleClose,
    setOpen
  };
};
